import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  archiveTest, deleteTest, duplicateTest, errorDetail, fetchOverviewDetail, hideTest, humanError, publishTest, unarchiveTest,
} from '../api';
import { Icon } from '../components/icons';
import { RestartTestPanel } from '../components/RestartTestPanel';
import { DeleteTestPanel } from '../components/DeleteTestPanel';
import { StatusPill } from '../components/StatusPill';
import { WorkspaceShell } from '../components/WorkspaceShell';
import { withAdminOnly } from '../components/withAdminOnly';
import {
  OverviewDetail, PASS_PERCENT, stateCardHint, stateCardText, statusLabel, statusSentence,
} from '../model';
import { pushNotice, takeNotice } from '../notices';
import type { WorkspaceNotice } from '../notices';
import { minutesLabel, questionsLabel } from '../words';
import { GivenTab } from './tabs/GivenTab';
import { QuestionsTab } from './tabs/QuestionsTab';
import { SettingsTab } from './tabs/SettingsTab';

type TabKey = 'questions' | 'given' | 'settings';
const TABS: TabKey[] = ['questions', 'given', 'settings'];

const TestDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const testId = Number(useParams().testId);
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const tab: TabKey = TABS.includes(rawTab as TabKey) ? (rawTab as TabKey) : 'questions';
  const [detail, setDetail] = useState<OverviewDetail | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'failed' | 'missing'>('loading');
  const [notice, setNotice] = useState<WorkspaceNotice | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [restartOpen, setRestartOpen] = useState(false);
  const [blocked, setBlocked] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const requestRef = useRef(0);
  const busyRef = useRef(false);

  useEffect(() => {
    // Не в инициализаторе useState: под <React.StrictMode> компонент рендерится дважды, второй вызов вернул бы null.
    const pending = takeNotice();
    if (pending) setNotice(pending);
  }, []);

  const reload = useCallback(async () => {
    const request = ++requestRef.current; // и неверный адрес отменяет ответы на прежние запросы: они не затрут «Такого теста нет»
    if (!Number.isInteger(testId) || testId < 1) {
      setState('missing'); // /admin/tests/abc: запрос с NaN сервер отклонил бы (422)
      return;
    }
    try {
      const data = await fetchOverviewDetail(testId);
      if (request !== requestRef.current) return;
      setDetail(data);
      setState('ready');
    } catch (error) {
      if (request !== requestRef.current) return;
      const status = (error as { response?: { status?: number } } | null)?.response?.status;
      setState(status === 404 ? 'missing' : 'failed');
    }
  }, [testId]);

  useEffect(() => {
    setState('loading');
    void reload();
  }, [reload]);

  const goTab = (key: TabKey) => setSearchParams({ tab: key }, { replace: true });

  const remove = async () => {
    if (!detail || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      await deleteTest(detail.id);
      pushNotice({ kind: 'ok', text: `Тест «${detail.title}» удалён.` });
      navigate('/admin/tests', { replace: true });
    } catch (error) {
      const status = (error as { response?: { status?: number } } | null)?.response?.status;
      if (status === 404) {
        pushNotice({ kind: 'info', text: 'Этот тест уже удалён. Список обновлён.' });
        navigate('/admin/tests', { replace: true });
      } else {
        setNotice({ kind: 'info', text: humanError(error, 'Не получилось удалить тест. Попробуйте ещё раз.') });
      }
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const run = async (action: () => Promise<void>, success: WorkspaceNotice | null, failure: string) => {
    // ref, а не состояние: два нажатия до перерисовки видят прежнее busy = false; состояние нужно только для `disabled`
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      await action();
      if (success) setNotice(success);
      setMenuOpen(false);
      setBlocked(null);
      await reload();
    } catch (error) {
      const info = errorDetail(error);
      if (info?.code === 'archive_blocked_by_live_assignments') setBlocked(Number(info.available) || 0);
      else setNotice({ kind: 'info', text: humanError(error, failure) });
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  if (state === 'loading') {
    return (
      <WorkspaceShell notice={notice} onCloseNotice={() => setNotice(null)}>
        <div className="tw-card tw-empty" aria-busy="true"><p className="tw-help">Загружаем тест…</p></div>
      </WorkspaceShell>
    );
  }
  if (state === 'missing' || state === 'failed' || detail === null) {
    return (
      <WorkspaceShell notice={notice} onCloseNotice={() => setNotice(null)}>
        <div className="tw-card tw-empty">
          <Icon name="info" size={44} large />
          <h1>{state === 'missing' ? 'Такого теста нет' : 'Не получилось загрузить тест'}</h1>
          <p className="tw-help">
            {state === 'missing' ? 'Возможно, его уже убрали. Вернитесь к списку тестов.' : 'Проверьте соединение и попробуйте ещё раз.'}
          </p>
          {state === 'failed' && (
            <button type="button" className="tw-btn tw-btn-primary" onClick={() => { setState('loading'); void reload(); }}>Повторить</button>
          )}
          <button type="button" className="tw-btn" onClick={() => navigate('/admin/tests')}>Все тесты</button>
        </div>
      </WorkspaceShell>
    );
  }

  const archived = detail.status === 'archived';
  const hint = stateCardHint(detail);
  const canPublish = detail.status === 'draft' && detail.assignments.total === 0;
  const hasQuestions = detail.questions_count > 0;
  const copyTest = () => run(
    async () => {
      const created = await duplicateTest(detail.id);
      setNotice({
        kind: 'ok',
        text: `Готово: создана копия «${created.title}». Она в состоянии «Готовится» — сотрудники её пока не видят.`,
      });
    },
    null,
    'Не получилось сделать копию. Попробуйте ещё раз.',
  );

  return (
    <WorkspaceShell notice={notice} onCloseNotice={() => setNotice(null)}>
      <section className="tw-screen">
        <div className="tw-row">
          <button type="button" className="tw-btn tw-btn-quiet" onClick={() => navigate('/admin/tests')}>
            <Icon name="left" />
            Все тесты
          </button>
        </div>

        <div>
          <h1>{detail.title}</h1>
          <div className="tw-row" style={{ marginTop: 12 }}>
            <StatusPill status={detail.status} label={statusLabel(detail)} />
            <span className="tw-meta">
              {`${questionsLabel(detail.questions_count)} · ${minutesLabel(detail.time_limit_minutes)} · сдать: от ${detail.passing_score ?? PASS_PERCENT}%`}
            </span>
          </div>
          <p className="tw-lead" style={{ marginTop: 12 }}>{statusSentence(detail)}</p>
        </div>

        <div className="tw-row tw-btn-row">
          {!archived && (
            <>
              <button type="button" className="tw-btn tw-btn-primary tw-btn-big" onClick={() => navigate(`/admin/tests/${detail.id}/assign`)}>
                Дать тест сотрудникам
              </button>
              <button type="button" className="tw-btn tw-btn-big" onClick={() => navigate(`/admin/tests/${detail.id}/questions/add`)}>
                <Icon name="plus" />
                Добавить вопросы
              </button>
              <button type="button" className="tw-btn tw-btn-big" onClick={() => setRestartOpen(!restartOpen)} aria-expanded={restartOpen}>
                Перезапустить сотруднику
              </button>
            </>
          )}
          <button
            type="button"
            className="tw-btn tw-btn-big"
            aria-expanded={menuOpen}
            disabled={busy}
            onClick={() => { setMenuOpen(!menuOpen); setBlocked(null); setDeleteOpen(false); }}
          >
            Ещё
            <Icon name="down" />
          </button>
        </div>

        {restartOpen && !archived && (
          <RestartTestPanel key={detail.id} testId={detail.id} testTitle={detail.title}
            onClose={() => setRestartOpen(false)}
            onDone={async (name) => { setRestartOpen(false); setNotice({ kind: 'ok', text: `Тест открыт заново для ${name}. При следующем запуске начнётся новая попытка с полным временем.` }); await reload(); }} />
        )}

        {menuOpen && (
          <div className="tw-menu">
            <div className="tw-menu-item">
              <button type="button" className="tw-btn" disabled={busy} onClick={() => void copyTest()}>
                <Icon name="copy" />
                Сделать копию
              </button>
              <span className="tw-help">Появится такой же тест в состоянии «Готовится». Сотрудники его не увидят.</span>
            </div>
            {!archived && (
              <div className="tw-menu-item">
                <button
                  type="button"
                  className="tw-btn"
                  disabled={busy}
                  onClick={() => void run(
                    () => archiveTest(detail.id).then(() => undefined),
                    { kind: 'ok', text: `Тест «${detail.title}» убран в архив. Сотрудники его больше не видят. Вернуть его можно в списке: кнопка «В архиве».` },
                    'Не получилось убрать тест в архив. Попробуйте ещё раз.',
                  )}
                >
                  <Icon name="archive" />
                  Убрать в архив
                </button>
                <span className="tw-help">Сотрудники перестанут видеть тест. Результаты сохранятся. Тест можно вернуть.</span>
              </div>
            )}
            {blocked !== null && (
              <div className="tw-warn">
                <Icon name="info" />
                <span>{`Сейчас тест доступен сотрудникам: ${blocked}. Сначала приостановите выдачи на вкладке «Кому выдан».`}</span>
                <button type="button" className="tw-btn tw-btn-small" onClick={() => { setMenuOpen(false); goTab('given'); }}>
                  Перейти к выдачам
                </button>
              </div>
            )}
            <div className="tw-menu-item">
              <button type="button" className="tw-btn tw-btn-danger-outline" disabled={busy}
                onClick={() => { setDeleteOpen(true); setBlocked(null); }}>
                <Icon name="x" />
                Удалить тест
              </button>
              <span className="tw-help">Удалит тест, его вопросы, назначения и результаты навсегда.</span>
            </div>
            {deleteOpen && <DeleteTestPanel test={detail} busy={busy} onConfirm={() => void remove()} onCancel={() => setDeleteOpen(false)} />}
          </div>
        )}

        <div className="tw-card tw-stack-sm">
          <h3>Состояние теста</h3>
          <p className="tw-help">{stateCardText(detail)}</p>
          <div className="tw-row tw-btn-row">
            {canPublish && (
              <button
                type="button"
                className="tw-btn"
                disabled={busy || !hasQuestions}
                onClick={() => void run(
                  () => publishTest(detail.id),
                  { kind: 'ok', text: 'Теперь тест видят все сотрудники.' },
                  'Не получилось открыть тест. Попробуйте ещё раз.',
                )}
              >
                Сделать доступным всем
              </button>
            )}
            {detail.status === 'public' && (
              <button
                type="button"
                className="tw-btn"
                disabled={busy}
                onClick={() => void run(
                  () => hideTest(detail.id),
                  { kind: 'ok', text: 'Тест снова в состоянии «Готовится»: сотрудники его не видят. Результаты тех, кто уже прошёл, сохранены.' },
                  'Не получилось спрятать тест. Попробуйте ещё раз.',
                )}
              >
                Спрятать от сотрудников
              </button>
            )}
            {(detail.status === 'assigned' || (detail.status === 'draft' && detail.assignments.total > 0)) && (
              <button type="button" className="tw-btn" onClick={() => goTab('given')}>Посмотреть, кому выдан</button>
            )}
            {archived && (
              <button
                type="button"
                className="tw-btn"
                disabled={busy}
                onClick={() => void run(
                  () => unarchiveTest(detail.id).then(() => undefined),
                  { kind: 'ok', text: `Тест «${detail.title}» вернулся из архива.` },
                  'Не получилось вернуть тест из архива. Попробуйте ещё раз.',
                )}
              >
                Вернуть из архива
              </button>
            )}
          </div>
          {canPublish && !hasQuestions && <p className="tw-help">Сначала добавьте хотя бы один вопрос.</p>}
          {hint && <p className="tw-help">{hint}</p>}
        </div>

        <div className="tw-tabs" role="group" aria-label="Разделы страницы теста">
          {TABS.map((key) => (
            <button key={key} type="button" className="tw-tab" aria-pressed={tab === key} onClick={() => goTab(key)}>
              {key === 'questions' ? `Вопросы (${detail.questions_count})` : key === 'given' ? 'Кому выдан' : 'Настройки'}
            </button>
          ))}
        </div>

        {tab === 'questions' && (
          <QuestionsTab
            detail={detail}
            reload={reload}
            notify={setNotice}
            onAdd={() => navigate(`/admin/tests/${detail.id}/questions/add`)}
            onCopyTest={() => void copyTest()}
          />
        )}
        {tab === 'given' && (
          <GivenTab detail={detail} reload={reload} notify={setNotice} onAssign={() => navigate(`/admin/tests/${detail.id}/assign`)} />
        )}
        {tab === 'settings' && <SettingsTab detail={detail} reload={reload} notify={setNotice} />}
      </section>
    </WorkspaceShell>
  );
};

export default withAdminOnly(TestDetailPage);
